
import React, { useState } from 'react';
import { Plus, ArrowRight, BrickWall, Hammer, Paintbrush, User, Users, Calendar, Lock, AlertCircle, X, MessageSquare, Edit3 } from 'lucide-react';
import { Project, ProjectType, PlanTier } from '../types';
import { formatCurrency } from '../utils/formatters';
import { APP_CONFIG } from '../constants';

interface ProjectListProps {
  projects: Project[];
  onSelect: (project: Project) => void;
  onNew: () => void;
  userName: string;
  planTier: PlanTier;
  onUpgrade: () => void;
}

export const ProjectList: React.FC<ProjectListProps> = ({ projects, onSelect, onNew, userName, planTier, onUpgrade }) => {
  const [showLimitModal, setShowLimitModal] = useState(false);
  
  // Limites por plano conforme APP_CONFIG
  const LIMITS = {
    trial: APP_CONFIG.MAX_FREE_PROJECTS,
    premium: APP_CONFIG.MAX_PREMIUM_PROJECTS,
    pro_plus: APP_CONFIG.MAX_PRO_PROJECTS
  };

  const currentLimit = LIMITS[planTier] || 1;
  const isAtLimit = projects.length >= currentLimit;
  const isAtHardLimit = projects.length >= 5; // Limite máximo do sistema para o plano mais alto

  const handleNewClick = () => {
    if (isAtHardLimit && planTier === 'pro_plus') {
        setShowLimitModal(true);
    } else if (isAtLimit) {
        onUpgrade();
    } else {
        onNew();
    }
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col px-6 py-12 animate-fadeIn">
      <div className="max-w-4xl mx-auto w-full">
        <div className="flex justify-between items-center mb-12">
           <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-[9px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest mb-3">
                 <User size={10} /> {userName.split(' ')[0]}
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-emerald-900 dark:text-white tracking-tighter leading-none">
                Minhas Obras
              </h1>
           </div>
           
           <button 
             onClick={handleNewClick}
             className={`size-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${!isAtLimit ? 'bg-emerald-600 text-white hover:bg-emerald-700 hover:scale-105' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-105'}`}
           >
             {!isAtLimit ? <Plus size={24} strokeWidth={3} /> : <Plus size={24} strokeWidth={3} />}
           </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
           {projects.map((project) => {
              const totalSpent = project.expenses.reduce((acc, curr) => acc + curr.amount, 0);
              const progress = project.initialBudget > 0 ? (totalSpent / project.initialBudget) * 100 : 0;

              return (
                <div 
                  key={project.id} 
                  onClick={() => onSelect(project)}
                  className="group bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border border-slate-100 dark:border-slate-800 shadow-xl hover:shadow-2xl hover:border-emerald-200 dark:hover:border-emerald-800/50 hover:-translate-y-1 transition-all cursor-pointer relative overflow-hidden"
                >
                   {/* Background Decor */}
                   <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 dark:bg-emerald-900/10 rounded-full blur-2xl -mr-10 -mt-10 transition-all group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/20"></div>

                   <div className="relative z-10 flex flex-col h-full">
                      <div className="flex justify-between items-start mb-6">
                         <div className={`size-14 rounded-2xl flex items-center justify-center shadow-lg text-white ${project.type === ProjectType.RENOVATION ? 'bg-indigo-500' : 'bg-emerald-600'}`}>
                            {project.logoUrl ? (
                               <img src={project.logoUrl} className="w-full h-full object-cover rounded-2xl" alt="" />
                            ) : (
                               project.type === ProjectType.RENOVATION ? <Paintbrush size={24} /> : <Hammer size={24} />
                            )}
                         </div>
                         <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-full border border-slate-100 dark:border-slate-700">
                            {project.collaborators && project.collaborators.length > 0 ? (
                               <Users size={12} className="text-slate-400" />
                            ) : (
                               <User size={12} className="text-slate-400" />
                            )}
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                               {project.collaborators && project.collaborators.length > 0 ? 'Compartilhado' : 'Pessoal'}
                            </span>
                         </div>
                      </div>

                      <div className="mb-6">
                         <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-1 truncate">{project.name}</h3>
                         <div className="flex items-center gap-2 text-slate-400">
                            <Calendar size={12} />
                            <span className="text-xs font-bold uppercase tracking-widest">Início: {new Date(project.startDate).toLocaleDateString('pt-BR')}</span>
                         </div>
                      </div>

                      <div className="mt-auto space-y-4">
                         <div className="flex justify-between items-end">
                            <div>
                               <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Executado</p>
                               <p className="text-lg font-black text-emerald-600 dark:text-emerald-400">{formatCurrency(totalSpent)}</p>
                            </div>
                            <div className="text-right">
                               <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Orçamento</p>
                               <p className="text-sm font-bold text-slate-600 dark:text-slate-300">{formatCurrency(project.initialBudget)}</p>
                            </div>
                         </div>

                         <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-1000 ${progress > 100 ? 'bg-red-500' : 'bg-emerald-500'}`} 
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                         </div>
                         
                         <div className="pt-2 flex items-center justify-end gap-2 text-emerald-600 dark:text-emerald-400 text-xs font-black uppercase tracking-widest group-hover:gap-3 transition-all">
                            Acessar Obra <ArrowRight size={14} />
                         </div>
                      </div>
                   </div>
                </div>
              );
           })}

           {/* Create New Card */}
           <button 
             onClick={handleNewClick}
             className="bg-white dark:bg-slate-900/50 rounded-[2.5rem] p-8 border-2 border-dashed border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-all group flex flex-col items-center justify-center gap-4 min-h-[300px]"
           >
              <div className={`size-16 rounded-full shadow-sm flex items-center justify-center transition-transform ${!isAtLimit ? 'bg-slate-50 dark:bg-slate-800 group-hover:scale-110' : 'bg-emerald-100 dark:bg-emerald-900/20 group-hover:scale-110'}`}>
                 {!isAtLimit ? (
                   <Plus size={28} className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
                 ) : (
                   <Plus size={28} className="text-emerald-600" />
                 )}
              </div>
              <div className="text-center">
                <p className={`text-sm font-black uppercase tracking-widest transition-colors ${!isAtLimit ? 'text-slate-400 group-hover:text-emerald-600' : 'text-emerald-600'}`}>
                    {isAtLimit ? 'Fazer Upgrade' : 'Iniciar Nova Obra'}
                </p>
                {isAtLimit && planTier !== 'pro_plus' && (
                   <p className="text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-wide">Acesso a mais obras</p>
                )}
                {isAtHardLimit && planTier === 'pro_plus' && (
                   <p className="text-[10px] font-bold text-rose-500 mt-2 uppercase tracking-wide">Limite Máximo Atingido</p>
                )}
              </div>
           </button>
        </div>
      </div>

      {/* Modal de Limite Máximo (5 Obras) */}
      {showLimitModal && (
        <div className="fixed inset-0 z-[400] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-6 animate-fadeIn" onClick={() => setShowLimitModal(false)}>
           <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[3rem] p-8 shadow-2xl relative overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="absolute top-0 right-0 p-8 opacity-5 text-emerald-600"><AlertCircle size={100}/></div>
              
              <button onClick={() => setShowLimitModal(false)} className="absolute top-6 right-6 p-2 bg-slate-50 dark:bg-slate-800 rounded-full"><X size={20}/></button>
              
              <div className="size-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-3xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/10">
                 <AlertCircle size={40} />
              </div>
              
              <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none mb-4">Limite de 5 Obras</h3>
              <p className="text-sm font-bold text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
                 Você atingiu o limite máximo do plano PRO+. Para gerenciar novas obras, você pode **arquivar ou excluir** um projeto antigo, ou entrar em contato para um **plano corporativo**.
              </p>
              
              <div className="space-y-3">
                 <button 
                   onClick={() => setShowLimitModal(false)}
                   className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-emerald-600/20 flex items-center justify-center gap-2"
                 >
                    <Edit3 size={16} /> Gerenciar Obras Antigas
                 </button>
                 <button 
                   className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                   onClick={() => window.open('mailto:suporte@minhaobra.shop?subject=Aumento de Limite de Obras')}
                 >
                    <MessageSquare size={16} /> Contatar Suporte
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};
